import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SEO from '@/components/feature/SEO';
import AdminPageEditBar from '@/components/feature/AdminPageEditBar';
import ConsultingHero, { ConsultingHeroData } from './components/ConsultingHero';
import ConsultingServices, { ConsultingServicesData } from './components/ConsultingServices';
import ConsultingWhoFor, { ConsultingWhoForData } from './components/ConsultingWhoFor';
import ConsultingCTA, { ConsultingProcessData } from './components/ConsultingCTA';

export interface ConsultingPageContent {
  hero: ConsultingHeroData;
  services: ConsultingServicesData;
  whoFor: ConsultingWhoForData;
  process: ConsultingProcessData;
}

export const defaultConsultingContentES: ConsultingPageContent = {
  hero: {
    badge: 'Consultoría de Negocios',
    titlePrefix: 'Construye tu Negocio ',
    titleHighlight: 'de la Manera Correcta',
    subtitle: 'Desde la formación de LLC y licencias hasta la estructura y estrategia empresarial — guiamos a emprendedores en cada paso.',
    ctaBook: 'Reservar una Consulta',
    ctaExplore: 'Explorar Servicios',
    bgImage: 'https://readdy.ai/api/search-image?query=professional%20business%20consulting%20meeting%20boardroom%20dark%20elegant%20atmosphere%20two%20entrepreneurs%20discussing%20strategy%20documents%20laptop%20modern%20office%20neon%20ambient%20lighting%20sophisticated%20corporate%20environment&width=1600&height=900&seq=bchero1&orientation=landscape',
    stats: [
      { value: 'LLC', label: 'Soporte de Formación' },
      { value: '100%', label: 'Guía Personalizada' },
      { value: 'Setup', label: 'Configuración de Negocio' }
    ]
  },
  services: {
    badge: 'Lo que Ofrecemos',
    titlePrefix: 'Servicios de ',
    titleHighlight: 'Consultoría Empresarial',
    subtitle: 'Orientación integral para ayudarte a construir, estructurar y hacer crecer un negocio legítimo y escalable.',
    items: [
      {
        icon: 'ri-building-2-line',
        title: 'Creación de Negocio y Formación de LLC',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=corporate%20legal%20documents%20LLC%20formation%20certificate%20fountain%20pen%20dark%20wood%20desk%20laptop%20modern%20office%20ambient%20neon%20lighting%20professional%20business%20setup&width=700&height=450&seq=llc1&orientation=landscape',
        desc: 'Te guiamos en el proceso de establecer tu entidad comercial — desde elegir la estructura hasta formar una LLC.',
        benefits: [
          'Guía sobre LLC vs otras estructuras',
          'Guía de formación paso a paso',
          'Acuerdos operativos',
          'Guía para EIN y cuentas bancarias',
          'Conexión con red profesional',
          'Empezar de la manera correcta'
        ],
        forWho: 'Emprendedores listos para formalizar.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Licencias y Cumplimiento',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=business%20compliance%20permits%20licenses%20checklist%20clipboard%20stamp%20dark%20office%20setting%20professional%20corporate%20advisor%20neon%20blue%20accents&width=700&height=450&seq=comp1&orientation=landscape',
        desc: 'Navega por las licencias y permisos comerciales. Te ayudamos a entender qué necesita tu negocio para operar legalmente.',
        benefits: [
          'Identificación de licencias',
          'Resumen de cumplimiento',
          'Lista de requisitos de permisos',
          'Guía de plazos y renovaciones',
          'Referencias de expertos',
          'Evitar complicaciones legales'
        ],
        forWho: 'Dueños que necesitan guía legal.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-line-chart-line',
        title: 'Estructura y Estrategia',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=business%20growth%20strategy%20chart%20analytics%20dashboard%20revenue%20scaling%20roadmap%20dark%20modern%20boardroom%20presentation%20screen%20neon%20magenta%20purple%20glow&width=700&height=450&seq=strat1&orientation=landscape',
        desc: 'Construye una base sólida con la estructura, sistemas y estrategia de crecimiento adecuados.',
        benefits: [
          'Diseño organizacional',
          'Estrategia de ingresos y precios',
          'Flujos de trabajo operativos',
          'Hoja de ruta de escalabilidad',
          'Marcos de objetivos y KPIs',
          'Posicionamiento competitivo'
        ],
        forWho: 'Dueños que buscan crecimiento sostenible.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-team-line',
        title: 'Red Profesional de Apoyo',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=professional%20network%20handshake%20business%20partners%20lawyer%20accountant%20consultant%20meeting%20modern%20glass%20office%20dark%20ambient%20lighting%20purple%20glow&width=700&height=450&seq=net1&orientation=landscape',
        desc: 'Accede a nuestra red de confianza de profesionales licenciados — abogados, contadores y más.',
        benefits: [
          'Referidos a abogados',
          'Conexiones con CPAs',
          'Asesores financieros',
          'Expertos específicos por industria',
          'Presentaciones directas',
          'Coordinación continua'
        ],
        forWho: 'Dueños que necesitan apoyo especializado.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      }
    ]
  },
  whoFor: {
    badge: 'Para Quién Es',
    titlePrefix: '¿Es Esto ',
    titleHighlight: 'Para Ti?',
    subtitle: 'Nuestros servicios de consultoría están diseñados para personas decididas y listas para construir algo real.',
    profiles: [
      {
        icon: 'ri-user-star-line',
        color: '#8c52ff',
        title: 'Emprendedores Primerizos',
        desc: 'Tienes una gran idea pero no sabes por dónde empezar. Te ayudamos a construir la base desde el primer día.'
      },
      {
        icon: 'ri-store-2-line',
        color: '#00c2ff',
        title: 'Dueños de Pequeños Negocios',
        desc: 'Ya estás operando pero necesitas formalizar, optimizar o escalar.'
      },
      {
        icon: 'ri-briefcase-line',
        color: '#FF2DAA',
        title: 'Freelancers que se Formalizan',
        desc: 'Estás listo para pasar de freelancer a dueño de negocio.'
      },
      {
        icon: 'ri-rocket-line',
        color: '#8c52ff',
        title: 'Negocios en Crecimiento',
        desc: 'Estás creciendo rápido y necesitas mejores sistemas y estructura.'
      },
      {
        icon: 'ri-global-line',
        color: '#00c2ff',
        title: 'Dueños de Negocios en Línea',
        desc: 'Negocios digitales que necesitan cumplimiento y estructura sólida.'
      },
      {
        icon: 'ri-map-pin-2-line',
        color: '#FF2DAA',
        title: 'Negocios de Servicios Locales',
        desc: 'Servicios locales que necesitan licencias y permisos para operar.'
      }
    ]
  },
  process: {
    badge: 'El Proceso',
    titlePrefix: 'Cómo ',
    titleHighlight: 'Funciona',
    subtitle: 'Un proceso simple diseñado para poner tu negocio en marcha — rápido.',
    steps: [
      {
        icon: 'ri-calendar-check-line',
        color: '#8c52ff',
        step: '01',
        title: 'Reservar una Consulta',
        desc: 'Agenda una sesión estratégica gratuita para hablar sobre tus metas.'
      },
      {
        icon: 'ri-search-eye-line',
        color: '#00c2ff',
        step: '02',
        title: 'Evaluación del Negocio',
        desc: 'Identificamos brechas y trazamos el camino correcto a seguir.'
      },
      {
        icon: 'ri-road-map-line',
        color: '#FF2DAA',
        step: '03',
        title: 'Plan de Acción Personalizado',
        desc: 'Recibe una hoja de ruta personalizada con pasos claros y prioridades.'
      },
      {
        icon: 'ri-rocket-2-line',
        color: '#8c52ff',
        step: '04',
        title: 'Ejecutar y Crecer',
        desc: 'Implementa tu plan con nuestro apoyo continuo.'
      }
    ],
    cta: {
      titlePrefix: '¿Listo para Construir tu Negocio ',
      titleHighlight: 'de la Manera Correcta?',
      subtitle: 'Deja de adivinar y empieza a construir con confianza. Reserva una consulta gratuita hoy mismo.',
      ctaBook: 'Reservar una Consulta Gratis',
      ctaServices: 'Ver Todos los Servicios'
    },
    disclaimer: {
      title: 'Aviso Legal',
      text: 'No ofrecemos asesoramiento legal ni financiero. Todas las decisiones deben tomarse en consulta con un profesional licenciado.'
    }
  }
};

export const defaultConsultingContentEN: ConsultingPageContent = {
  hero: {
    badge: 'Business Consulting',
    titlePrefix: 'Build Your Business ',
    titleHighlight: 'the Right Way',
    subtitle: 'From LLC formation and licensing to business structure and strategy — we guide entrepreneurs at every step.',
    ctaBook: 'Book a Consultation',
    ctaExplore: 'Explore Services',
    bgImage: 'https://readdy.ai/api/search-image?query=professional%20business%20consulting%20meeting%20boardroom%20dark%20elegant%20atmosphere%20two%20entrepreneurs%20discussing%20strategy%20documents%20laptop%20modern%20office%20neon%20ambient%20lighting%20sophisticated%20corporate%20environment&width=1600&height=900&seq=bchero1&orientation=landscape',
    stats: [
      { value: 'LLC', label: 'Formation Support' },
      { value: '100%', label: 'Personalized Guidance' },
      { value: 'Setup', label: 'Business Setup' }
    ]
  },
  services: {
    badge: 'What We Offer',
    titlePrefix: 'Business ',
    titleHighlight: 'Consulting Services',
    subtitle: 'Comprehensive guidance to help you build, structure, and grow a legitimate and scalable business.',
    items: [
      {
        icon: 'ri-building-2-line',
        title: 'Business Setup & LLC Formation',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=corporate%20legal%20documents%20LLC%20formation%20certificate%20fountain%20pen%20dark%20wood%20desk%20laptop%20modern%20office%20ambient%20neon%20lighting%20professional%20business%20setup&width=700&height=450&seq=llc1&orientation=landscape',
        desc: 'We guide you through establishing your business entity — from choosing the right structure to forming an LLC.',
        benefits: [
          'Guidance on LLC vs other structures',
          'Step-by-step formation guide',
          'Operating agreements',
          'EIN and bank account guidance',
          'Professional network connection',
          'Starting the right way'
        ],
        forWho: 'Entrepreneurs ready to formalize.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Licensing & Compliance',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=business%20compliance%20permits%20licenses%20checklist%20clipboard%20stamp%20dark%20office%20setting%20professional%20corporate%20advisor%20neon%20blue%20accents&width=700&height=450&seq=comp1&orientation=landscape',
        desc: 'Navigate business licenses and permits. We help you understand what your business needs to operate legally.',
        benefits: [
          'License identification',
          'Compliance overview',
          'Permit requirement checklist',
          'Deadlines & renewal guide',
          'Expert referrals',
          'Avoid legal headaches'
        ],
        forWho: 'Owners needing legal guidance.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-line-chart-line',
        title: 'Structure & Strategy',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=business%20growth%20strategy%20chart%20analytics%20dashboard%20revenue%20scaling%20roadmap%20dark%20modern%20boardroom%20presentation%20screen%20neon%20magenta%20purple%20glow&width=700&height=450&seq=strat1&orientation=landscape',
        desc: 'Build a solid foundation with the right structure, systems, and growth strategy for your business.',
        benefits: [
          'Organizational design',
          'Revenue & pricing strategy',
          'Operational workflows',
          'Scalability roadmap',
          'Goal setting & KPIs',
          'Competitive positioning'
        ],
        forWho: 'Owners looking for sustainable growth.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-team-line',
        title: 'Professional Support Network',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=professional%20network%20handshake%20business%20partners%20lawyer%20accountant%20consultant%20meeting%20modern%20glass%20office%20dark%20ambient%20lighting%20purple%20glow&width=700&height=450&seq=net1&orientation=landscape',
        desc: 'Access our trusted network of licensed professionals — attorneys, CPAs, and business advisors.',
        benefits: [
          'Attorney referrals',
          'CPA connections',
          'Financial advisors',
          'Industry-specific experts',
          'Direct introductions',
          'Ongoing coordination'
        ],
        forWho: 'Owners needing specialized legal/tax support.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      }
    ]
  },
  whoFor: {
    badge: 'Who It Is For',
    titlePrefix: 'Is This ',
    titleHighlight: 'For You?',
    subtitle: 'Our consulting services are designed for driven individuals ready to build something real.',
    profiles: [
      {
        icon: 'ri-user-star-line',
        color: '#8c52ff',
        title: 'First-Time Entrepreneurs',
        desc: "You have a great idea but don't know where to start. We help you build the foundation from day one."
      },
      {
        icon: 'ri-store-2-line',
        color: '#00c2ff',
        title: 'Small Business Owners',
        desc: 'Already operating but need to formalize, optimize, or scale.'
      },
      {
        icon: 'ri-briefcase-line',
        color: '#FF2DAA',
        title: 'Freelancers Going Official',
        desc: 'You are ready to transition from a freelancer to a legitimate business owner.'
      },
      {
        icon: 'ri-rocket-line',
        color: '#8c52ff',
        title: 'Growing Businesses',
        desc: 'You are scaling quickly and need better systems and organizational structure.'
      },
      {
        icon: 'ri-global-line',
        color: '#00c2ff',
        title: 'Online Business Owners',
        desc: 'Digital businesses that need compliance and a rock-solid business structure.'
      },
      {
        icon: 'ri-map-pin-2-line',
        color: '#FF2DAA',
        title: 'Local Service Businesses',
        desc: 'Local service companies needing licenses and permits to operate legally.'
      }
    ]
  },
  process: {
    badge: 'The Process',
    titlePrefix: 'How It ',
    titleHighlight: 'Works',
    subtitle: 'A straightforward process designed to get your business up and running — fast.',
    steps: [
      {
        icon: 'ri-calendar-check-line',
        color: '#8c52ff',
        step: '01',
        title: 'Book a Consultation',
        desc: 'Schedule a free strategy session to discuss your business goals.'
      },
      {
        icon: 'ri-search-eye-line',
        color: '#00c2ff',
        step: '02',
        title: 'Business Assessment',
        desc: 'We identify operational gaps and chart the best path forward.'
      },
      {
        icon: 'ri-road-map-line',
        color: '#FF2DAA',
        step: '03',
        title: 'Custom Action Plan',
        desc: 'Receive a personalized roadmap with clear steps, priorities, and deadlines.'
      },
      {
        icon: 'ri-rocket-2-line',
        color: '#8c52ff',
        step: '04',
        title: 'Execute & Grow',
        desc: 'Implement your plan with our continuous guidance and support.'
      }
    ],
    cta: {
      titlePrefix: 'Ready to Build Your Business ',
      titleHighlight: 'the Right Way?',
      subtitle: 'Stop guessing and start building with confidence. Book a free consultation today.',
      ctaBook: 'Book a Free Consultation',
      ctaServices: 'View All Services'
    },
    disclaimer: {
      title: 'Legal Disclaimer',
      text: 'We do not offer legal or financial advice. All decisions should be made in consultation with a licensed professional.'
    }
  }
};

const BusinessConsultingPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';
  const docId = lang === 'en' ? 'consulting_page_en' : 'consulting_page';
  const defaultContent = lang === 'en' ? defaultConsultingContentEN : defaultConsultingContentES;

  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [consultingUrl, setConsultingUrl] = useState('/contact');
  const [content, setContent] = useState<ConsultingPageContent>(defaultContent);
  const [initialContent, setInitialContent] = useState<ConsultingPageContent>(defaultContent);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNotification, setSavedNotification] = useState(false);

  useEffect(() => {
    // Reset immediately to the target language defaults while fetching
    setContent(defaultContent);
    setInitialContent(defaultContent);

    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'general');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists() && docSnap.data().consultingUrl) {
          setConsultingUrl(docSnap.data().consultingUrl);
        }
      } catch (error) {
        console.error('Error fetching consultingUrl:', error);
      }
    };

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<ConsultingPageContent>;
          const merged: ConsultingPageContent = {
            hero: { ...defaultContent.hero, ...(data.hero || {}), stats: data.hero?.stats || defaultContent.hero.stats },
            services: {
              ...defaultContent.services,
              ...(data.services || {}),
              items: data.services?.items || defaultContent.services.items
            },
            whoFor: {
              ...defaultContent.whoFor,
              ...(data.whoFor || {}),
              profiles: data.whoFor?.profiles || defaultContent.whoFor.profiles
            },
            process: {
              ...defaultContent.process,
              ...(data.process || {}),
              steps: data.process?.steps || defaultContent.process.steps,
              cta: {
                ...defaultContent.process.cta,
                ...(data.process?.cta || {})
              },
              disclaimer: {
                ...defaultContent.process.disclaimer,
                ...(data.process?.disclaimer || {})
              }
            }
          };
          setContent(merged);
          setInitialContent(merged);
        }
      } catch (error) {
        console.error('Error fetching consulting page content:', error);
      }
    };

    fetchSettings();
    fetchContent();
  }, [lang, docId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_content', docId);
      await setDoc(docRef, content);
      setInitialContent(content);
      setIsEditing(false);
      setSavedNotification(true);
      setTimeout(() => setSavedNotification(false), 3500);
    } catch (error) {
      console.error('Error saving consulting page content:', error);
      alert(lang === 'en' ? 'Error saving changes to Firestore.' : 'Error al guardar los cambios en Firestore.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent);
    setIsEditing(false);
  };

  return (
    <main className="relative">
      <SEO 
        title={lang === 'en' ? "Business Consulting | Sbdigisolutions" : "Consultoría de Negocios | Sbdigisolutions"} 
        description={lang === 'en' ? "Comprehensive business consulting for LLC formation, licensing, strategy, and business structure." : "Consultoría integral para emprendedores: formación de LLC, licencias, cumplimiento y estructura empresarial."} 
      />

      <AdminPageEditBar
        isAdmin={isAdmin}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        pageName={lang === 'en' ? 'Consulting (EN)' : 'Consultoría (ES)'}
      />

      {savedNotification && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-2xl flex items-center gap-2 animate-bounce">
          <i className="ri-checkbox-circle-line text-lg" />
          <span>{lang === 'en' ? 'Business Consulting page saved successfully!' : '¡Página de Consultoría guardada correctamente!'}</span>
        </div>
      )}

      <ConsultingHero
        data={content.hero}
        consultingUrl={consultingUrl}
        isEditing={isEditing}
        onChange={(hero) => setContent({ ...content, hero })}
      />

      <ConsultingServices
        data={content.services}
        consultingUrl={consultingUrl}
        isEditing={isEditing}
        onChange={(services) => setContent({ ...content, services })}
      />

      <ConsultingWhoFor
        data={content.whoFor}
        isEditing={isEditing}
        onChange={(whoFor) => setContent({ ...content, whoFor })}
      />

      <ConsultingCTA
        data={content.process}
        consultingUrl={consultingUrl}
        isEditing={isEditing}
        onChange={(process) => setContent({ ...content, process })}
      />
    </main>
  );
};

export default BusinessConsultingPage;
